import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	queryParamsToFilters,
	updateUrlWithFilters
} from 'src/common/helpers/FilterQueryParamsHelper';

interface UseFiltersUrlSyncParams {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
}

interface UseFiltersUrlSyncReturn {
	filtersFromUrl: IFilterOptions;
	handleFilterChange: (filters: IFilterOptions) => void;
}

const getDefaultFilters = (filterDefinitions: IFilterDefinition[]): IFilterOptions =>
	filterDefinitions.reduce<IFilterOptions>((acc, def) => {
		const val = def.defaultValue;
		const isEmpty =
			val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
		if (!isEmpty) {
			acc[def.id] = val as string | OptionType[];
		}
		return acc;
	}, {});

export const useFiltersUrlSync = ({
	filterDefinitions,
	filterOptions
}: UseFiltersUrlSyncParams): UseFiltersUrlSyncReturn => {
	const location = useLocation();
	const history = useHistory();

	const filtersFromUrl = useMemo(() => {
		if (!filterDefinitions.length || !Object.keys(filterOptions).length) {
			return {};
		}
		if (location.search) {
			return queryParamsToFilters(
				new URLSearchParams(location.search),
				filterDefinitions,
				filterOptions
			);
		}
		return getDefaultFilters(filterDefinitions);
	}, [location.search, filterDefinitions, filterOptions]);

	const handleFilterChange = useCallback(
		(filters: IFilterOptions): void => {
			updateUrlWithFilters(history, location, filters, filterDefinitions);
		},
		[history, location, filterDefinitions]
	);

	return { filtersFromUrl, handleFilterChange };
};
